import { SYMPY_UNIT_TO_DISPLAY } from "../unitToSympyMap";
import { numberNode } from "../utils";

import { buildUnitNode } from "./shared";

import type { UnitArgInfo } from "./shared";
import type { SympyParser } from "./SympyParser";
import type { SympyResult } from "../types";
import type { NodeJson } from "@corca/data";

export function parseMultiplication(parser: SympyParser, args: SympyResult[]): NodeJson[] {
  // Separate unit args from numeric args
  const unitInfos: UnitArgInfo[] = [];
  const unitArgIndices = new Set<number>();

  for (let i = 0; i < args.length; i++) {
    const info = getUnitInfoFromArg(args[i]);
    if (info) {
      unitInfos.push(info);
      unitArgIndices.add(i);
    }
  }

  if (unitInfos.length > 0) {
    const nonUnitArgs = args.filter((_, i) => !unitArgIndices.has(i));
    const numericNodes =
      nonUnitArgs.length > 0 ? parser.parseMultiplication(nonUnitArgs) : [numberNode("1")];

    if (numericNodes.length === 1 && numericNodes[0].type === "number") {
      return [{ ...numericNodes[0], children: [buildUnitNode(unitInfos)] }];
    }
    return [...numericNodes, buildUnitNode(unitInfos)];
  }

  // Pure product renderer: reciprocal factors (1/d) were already folded into a Frac by
  // normalizeSemantic, so a normalized Mul carries only numerator factors.
  const numeratorNodes: NodeJson[] = [];
  for (let i = 0; i < args.length; i++) {
    numeratorNodes.push(...parser.parseResult(args[i], { grouped: true }));
  }
  return numeratorNodes;
}

/** Extract unit info from a SympyResult (bare unit or Pow(unit, n)). */
function getUnitInfoFromArg(arg: SympyResult): UnitArgInfo | null {
  if (Array.isArray(arg)) return null;

  if (arg.type === "unit") {
    const display = SYMPY_UNIT_TO_DISPLAY[arg.name];
    return display ? { display, exponent: 1 } : null;
  }

  if (
    arg.type === "operator" &&
    arg.name === "Pow" &&
    arg.args.length === 2 &&
    !Array.isArray(arg.args[0]) &&
    arg.args[0].type === "unit" &&
    !Array.isArray(arg.args[1]) &&
    arg.args[1].type === "number"
  ) {
    const display = SYMPY_UNIT_TO_DISPLAY[arg.args[0].name];
    return display ? { display, exponent: Number(arg.args[1].value) } : null;
  }

  return null;
}
